'use client';

/**
 * CoverageKpiTile — shared visual primitive for the coverage KPI cards.
 * Each KPI card is a thin shell that calls useCoverageView, slices its
 * fields, and renders one of these tiles.
 *
 * Typography via tokens.ts (`text` / `ink`) — the current chrome lane;
 * NOT the deprecated useTypographyBundle. Container chrome (icon chip,
 * sub chip, padding, hover) lives inline here per the mode7 rule.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { type LucideIcon } from 'lucide-react';
import type { QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { useCardExport, paintedKpi } from '@/lib/design/card-export';

interface CoverageKpiTileProps {
  label: string;
  Icon: LucideIcon;
  /** Hero value AS PAINTED ("0.6%", "1", "177"); '…' while loading. */
  value: string;
  sub?: string;
  /** Accent the hero number (e.g. the headline coverage %). */
  accent?: boolean;
  contentKey: string;
  queryKey: QueryKey;
}

export function CoverageKpiTile({
  label, Icon, value, sub, accent, contentKey, queryKey,
}: CoverageKpiTileProps) {
  useCardExport(paintedKpi({ label, value, sub, content_key: contentKey, query_key: queryKey }));
  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm h-full flex flex-col group hover:border-primary/30 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
          <Icon className="w-5 h-5" />
        </div>
        {sub && (
          <span className={cn(text.muted, 'font-medium bg-muted px-2 py-1 rounded-md')}>
            {sub}
          </span>
        )}
      </div>
      <p className={cn(text.value, accent && 'text-primary')}>{value}</p>
      <p className={cn(text.muted, 'mt-1 font-medium')}>{label}</p>
    </div>
  );
}
